import React, { useState } from "react";
import { Button, Form, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnergyPerformanceInstallationform } from "../../../../api";
import { INSTALLATION_ARCHETYPES } from "../catalogs/installationArchetypes";
import { P, useEnergia, useGuardarSeccion, useSoloLectura } from "../engine";
import { useBorrador } from "../common/useBorrador";
import { NumField } from "../common/NumField";
import { EditableTable } from "../common/EditableTable";
import { Notice, SaveBar, SaveErrors, Section } from "../common/SectionCommon";
import "../common/common.scss";
import "./InstallationProfileForm.scss";

const energyPerformanceCtrl = new EnergyPerformanceInstallationform();

// Partes de la instalación (formato v2) que se editan en la ficha.
const CAMPOS_FICHA = ["name", "country", "sector", "arquetipo", "unidadProd", "unidadProdCorta", "area", "employees", "shifts",
  "alcance", "lines", "processes", "fuentes", "tarifas", "factores", "combustibles", "conversiones", "metricParams"];
const tomarFicha = (inst) => {
  const b = Object.fromEntries(CAMPOS_FICHA.map((k) => [k, inst[k]]));
  // en el archivo cada fuente es una tupla [nombre, uso principal, costo]; para editarla, un objeto
  b.fuentes = (inst.fuentes || []).map(([nombre, uso, costo]) => ({ nombre, uso, costo }));
  b.tarifas = { ...(inst.tarifas || {}) };
  b.factores = { ...(inst.factores || {}) };
  b.lines = [...(inst.lines || [])];
  b.processes = [...(inst.processes || [])];
  b.combustibles = inst.combustibles || [];
  b.conversiones = inst.conversiones || [];
  return b;
};

// Lista de textos como etiquetas editables (líneas productivas, procesos).
function PillList({ values, onChange, addLabel, ariaLabel }) {
  return (
    <div className="ep-pills">
      {values.map((v, i) => (
        <span className="ep-pill" key={i}>
          <input type="text" aria-label={`${ariaLabel} ${i + 1}`} value={v} style={{ width: `${Math.max(10, v.length + 2)}ch` }} onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))} />
          <button type="button" aria-label={`× ${v}`} onClick={() => onChange(values.filter((_, j) => j !== i))}>×</button>
        </span>
      ))}
      <Button type="button" basic size="mini" onClick={() => onChange([...values, ""])}><Icon name="plus" /> {addLabel}</Button>
    </div>
  );
}

// Alta de una instalación nueva (solo lo mínimo; el resto se completa acá mismo o importando el modelo).
function CrearInstalacion({ siteSelected, onSaved, t }) {
  const { accessToken, user } = useAuth();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const sinPermiso = useSoloLectura() === "permiso";
  const crear = async () => {
    if (!name.trim() || sinPermiso) return;
    setSaving(true);
    try {
      const res = await energyPerformanceCtrl.createEnergyPerformanceInstallation(accessToken, { name: name.trim(), site: siteSelected || user?.site });
      toast.success(t("form_saved_successfully"));
      onSaved?.(res.installation);
    } catch (error) {
      toast.error(error?.message || error?.msg || t("server_connection_error"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="ep-screen">
      <h3 className="ep-screen__title">{t("energy_performance_profile_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_profile_create_intro")}</p>
      <Form onSubmit={crear}>
        <Form.Input id="ep-new-name" label={t("energy_performance_installation_name")} value={name} onChange={(_, { value }) => setName(value)} required />
        {sinPermiso && <p className="ep-section__note">{t("energy_performance_no_permission")}</p>}
        <Button type="submit" primary loading={saving} disabled={!name.trim() || saving || sinPermiso}>{t("energy_performance_create_installation")}</Button>
      </Form>
    </div>
  );
}

// Ficha de la instalación y alcance del SGEn (hoja 02 «ficha»; hoja 01 filas 44, 84, 138, 148, 163).
export function InstallationProfileForm({ installation, siteSelected, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { inst, f } = useEnergia(installation);
  const { borrador: b, cambiar, dirty, descartar, marcarInvalidos, invalidos, version } = useBorrador(inst, tomarFicha);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });

  if (!installation) return <CrearInstalacion siteSelected={siteSelected} onSaved={onSaved} t={t} />;
  if (!b) return null;

  // vista con el borrador (para el sector sugerido y si corresponden los campos de confección)
  const vista = { ...inst, ...b };
  const textil = P.esTextil(vista);
  const conPrenda = textil && P.esPrenda(vista);
  const sugerido = P.sectorSugerido({ ...vista, sector: "" });
  const ch = f ? P.costoYHuella(f) : null;
  const num = (clave, etiqueta) => (
    <NumField key={`${version}-${clave}`} value={b[clave]} campo={clave} ariaLabel={etiqueta}
      onChange={(v) => cambiar(clave, v)} onError={(e) => marcarInvalidos(clave, e ? 1 : 0)} />
  );
  const numEn = (obj, clave, campo, etiqueta) => (
    <NumField key={`${version}-${obj}.${clave}`} value={b[obj] && b[obj][clave]} campo={campo} ariaLabel={etiqueta}
      onChange={(v) => cambiar(obj, { ...b[obj], [clave]: v })} onError={(e) => marcarInvalidos(`${obj}.${clave}`, e ? 1 : 0)} />
  );

  const onSave = () => {
    const cambios = { ...b, fuentes: b.fuentes.map((x) => [x.nombre || "", x.uso || "", x.costo || ""]) };
    if (!conPrenda) delete cambios.metricParams;
    guardar(cambios);
  };

  return (
    <div className="ep-screen energy-performance-profile">
      <h3 className="ep-screen__title">{t("energy_performance_profile_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_profile_intro")}</p>

      <Section title={t("energy_performance_profile_identification")}>
        <div className="ep-grid">
          <div>
            <label>{t("energy_performance_field_code")}</label>
            <span className="ep-mono">{installation.code}</span>
          </div>
          <div>
            <label htmlFor="ep-name">{t("energy_performance_installation_name")}</label>
            <input id="ep-name" type="text" value={b.name || ""} onChange={(e) => cambiar("name", e.target.value)} />
          </div>
          <div>
            <label htmlFor="ep-country">{t("country")}</label>
            <input id="ep-country" type="text" value={b.country || ""} onChange={(e) => cambiar("country", e.target.value)} />
          </div>
          <div>
            <label htmlFor="ep-sector">{t("energy_performance_field_sector")}</label>
            <select id="ep-sector" value={b.sector || ""} onChange={(e) => cambiar("sector", e.target.value)}>
              {P.SECTORES.map((s) => <option key={s} value={s}>{s || `${t("energy_performance_sector_unset")} (${t("energy_performance_suggested")}: ${sugerido})`}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="ep-arch">{t("energy_performance_archetype")}</label>
            <input id="ep-arch" type="text" list="ep-arquetipos" value={b.arquetipo || ""} onChange={(e) => cambiar("arquetipo", e.target.value)} />
            <datalist id="ep-arquetipos">{INSTALLATION_ARCHETYPES.map((a) => <option key={a} value={a} />)}</datalist>
          </div>
          <div>
            <label htmlFor="ep-unit">{t("energy_performance_production_unit")}</label>
            <input id="ep-unit" type="text" value={b.unidadProd || ""} onChange={(e) => cambiar("unidadProd", e.target.value)} />
          </div>
          <div>
            <label htmlFor="ep-unit-short">{t("energy_performance_production_unit_short")}</label>
            <input id="ep-unit-short" type="text" value={b.unidadProdCorta || ""} onChange={(e) => cambiar("unidadProdCorta", e.target.value)} />
          </div>
          <div><label>{t("energy_performance_area")}</label>{num("area", t("energy_performance_area"))}</div>
          <div><label>{t("energy_performance_employees")}</label>{num("employees", t("energy_performance_employees"))}</div>
          <div><label>{t("energy_performance_shifts")}</label>{num("shifts", t("energy_performance_shifts"))}</div>
          {conPrenda && (
            <>
              <div><label>{t("energy_performance_field_sam")}</label>{numEn("metricParams", "sam", "sam", "SAM")}</div>
              <div><label>{t("energy_performance_field_kg_garment")}</label>{numEn("metricParams", "kgPorPrenda", "kgPorPrenda", "kg/prenda")}</div>
            </>
          )}
        </div>
        {f && f._avisoUnidad && <Notice titulo={t("energy_performance_notice")}>{t("energy_performance_unit_missing")}</Notice>}
      </Section>

      <Section title={t("energy_performance_lines")}>
        <PillList values={b.lines} onChange={(v) => cambiar("lines", v)} addLabel={t("energy_performance_add_line")} ariaLabel={t("energy_performance_lines")} />
        <h4 className="ep-section__title ep-profile__subtitle">{t("energy_performance_processes")}</h4>
        <PillList values={b.processes} onChange={(v) => cambiar("processes", v)} addLabel={t("energy_performance_add_process")} ariaLabel={t("energy_performance_processes")} />
      </Section>

      <Section title={t("energy_performance_scope")} note={t("energy_performance_scope_rule")}>
        <textarea aria-label={t("energy_performance_scope")} rows={4} className="ep-profile__scope" value={b.alcance || ""} onChange={(e) => cambiar("alcance", e.target.value)} placeholder={t("energy_performance_scope_placeholder")} />
      </Section>

      <Section title={t("energy_performance_sources")}>
        <EditableTable
          key={`fuentes-${version}`}
          t={t}
          rows={b.fuentes}
          onChange={(v) => cambiar("fuentes", v)}
          newRow={() => ({ nombre: "", uso: "", costo: "" })}
          addLabel={t("energy_performance_add_source")}
          columns={[
            { key: "nombre", label: t("energy_performance_source_name") },
            { key: "uso", label: t("energy_performance_source_main_use") },
            { key: "costo", label: t("energy_performance_source_unit_cost") },
          ]}
        />
      </Section>

      <Section title={t("energy_performance_rates_title")} note={t("energy_performance_rates_note")}>
        <div className="ep-grid">
          <div><label>{t("energy_performance_tariff_elec")}</label>{numEn("tarifas", "elec", "elec", t("energy_performance_tariff_elec"))}</div>
          <div><label>{t("energy_performance_tariff_term")}</label>{numEn("tarifas", "term", "term", t("energy_performance_tariff_term"))}</div>
          <div><label>{t("energy_performance_factor_elec")}</label>{numEn("factores", "elec", "elec", t("energy_performance_factor_elec"))}</div>
          <div><label>{t("energy_performance_factor_term")}</label>{numEn("factores", "term", "term", t("energy_performance_factor_term"))}</div>
        </div>
        <h4 className="ep-section__title ep-profile__subtitle">{t("energy_performance_fuels_title")}</h4>
        <p className="ep-section__note">{t("energy_performance_fuels_note")}</p>
        <EditableTable
          key={`comb-${version}`}
          t={t}
          rows={b.combustibles}
          onChange={(v) => cambiar("combustibles", v)}
          onErrors={(n) => marcarInvalidos("combustibles", n)}
          newRow={() => P.ROW_TEMPLATES.combustibles()}
          addLabel={t("energy_performance_add_fuel")}
          emptyText={t("energy_performance_fuels_empty")}
          columns={[
            { key: "nombre", label: t("energy_performance_fuel") },
            { key: "tarifa", label: t("energy_performance_fuel_rate"), type: "num" },
            { key: "factor", label: t("energy_performance_fuel_factor"), type: "num" },
            { key: "respaldo", label: t("energy_performance_fuel_backup") },
          ]}
        />
        {ch && ch.generico && (
          <Notice titulo={t("energy_performance_several_fuels")}>{t("energy_performance_several_fuels_text").replace("{fuels}", P.termDistintos(f).join(", "))}</Notice>
        )}
        {ch && ch.mixtoAsumido && <Notice titulo={t("energy_performance_mixed_source")}>{t("energy_performance_mixed_source_text")}</Notice>}
      </Section>

      <Section title={t("energy_performance_conversions_title")} note={t("energy_performance_conversions_note")}>
        <EditableTable
          key={`conv-${version}`}
          t={t}
          rows={b.conversiones}
          onChange={(v) => cambiar("conversiones", v)}
          onErrors={(n) => marcarInvalidos("conversiones", n)}
          newRow={() => P.ROW_TEMPLATES.conversiones()}
          addLabel={t("energy_performance_add_conversion")}
          emptyText={t("energy_performance_conversions_empty")}
          columns={[
            { key: "combustible", label: t("energy_performance_fuel") },
            { key: "pci", label: t("energy_performance_heating_value"), type: "num" },
            { key: "unidad", label: t("energy_performance_unit") },
            { key: "fuente", label: t("energy_performance_data_source") },
          ]}
        />
      </Section>

      {textil && (inst.femTypes || []).length > 0 && (
        <Section title={t("energy_performance_fem_title")} note={inst.femNota}>
          {inst.femTypes.map((tipo) => {
            const T = P.FEM_TYPES[tipo];
            if (!T) return null;
            return (
              <div className="ep-profile__fem" key={tipo}>
                <strong>{t("energy_performance_fem_type")} {tipo} · {T.nombre}</strong>
                <p>{T.desc}</p>
                <p className="ep-section__note"><strong>{t("energy_performance_fem_focus")}:</strong> {T.focoEnergia}</p>
              </div>
            );
          })}
        </Section>
      )}

      <SaveErrors errores={errores} t={t} />
      <SaveBar dirty={dirty} invalidos={invalidos} saving={saving} onSave={onSave} onDiscard={descartar} t={t} />
    </div>
  );
}
